import { WhatsAppState } from '../types';
import * as actions from '../actions';

const PINNED_CONVERSATIONS_KEY = 'whatsapp_pinned_conversations';

const loadPinnedFromLocalStorage = (): string[] => {
  try {
    const stored = localStorage.getItem(PINNED_CONVERSATIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const savePinnedToLocalStorage = (ids: string[]) => {
  try {
    localStorage.setItem(PINNED_CONVERSATIONS_KEY, JSON.stringify(ids));
  } catch {
    // Ignore errors
  }
};

const initialState: WhatsAppState = {
  connections: {
    byId: {},
    allIds: [],
    isLoading: false,
    error: null
  },
  contacts: {
    byId: {},
    allIds: [],
    isLoading: false,
    error: null
  },
  conversations: {
    byId: {},
    allIds: [],
    selectedConversationId: null,
    pinnedConversationIds: loadPinnedFromLocalStorage(),
    timeline: {
      messages: [],
      alerts: [],
      callEvents: [],
      groupMembers: [],
      isLoading: false,
      currentPage: 1,
      hasMore: true
    },
    isLoading: false,
    error: null
  },
  templates: {
    items: [],
    isLoading: false,
    error: null
  }
};

export const whatsappReducer = (state = initialState, action: any): WhatsAppState => {
  switch (action.type) {
    // ========== Connections ==========
    case actions.FETCH_CONNECTIONS_REQUEST:
    case actions.INSERT_CONNECTION_REQUEST:
    case actions.UPDATE_CONNECTION_REQUEST:
      return {
        ...state,
        connections: {
          ...state.connections,
          isLoading: true,
          error: null
        }
      };

    case actions.FETCH_CONNECTIONS_SUCCESS:
      return {
        ...state,
        connections: {
          byId: action.payload.byId,
          allIds: action.payload.allIds,
          isLoading: false,
          error: null
        }
      };

    case actions.INSERT_CONNECTION_SUCCESS:
    case actions.UPDATE_CONNECTION_SUCCESS:
      return {
        ...state,
        connections: {
          ...state.connections,
          byId: {
            ...state.connections.byId,
            [action.payload.conversationsConnectionId]: action.payload
          },
          allIds: state.connections.allIds.includes(action.payload.conversationsConnectionId)
            ? state.connections.allIds
            : [...state.connections.allIds, action.payload.conversationsConnectionId],
          isLoading: false,
          error: null
        }
      };

    case actions.FETCH_CONNECTIONS_FAILURE:
    case actions.INSERT_CONNECTION_FAILURE:
    case actions.UPDATE_CONNECTION_FAILURE:
      return {
        ...state,
        connections: {
          ...state.connections,
          isLoading: false,
          error: action.payload
        }
      };

    // ========== Contacts ==========
    case actions.FETCH_CONTACTS_REQUEST:
    case actions.INSERT_CONTACT_REQUEST:
    case actions.UPDATE_CONTACT_REQUEST:
      return {
        ...state,
        contacts: {
          ...state.contacts,
          isLoading: true,
          error: null
        }
      };

    case actions.FETCH_CONTACTS_SUCCESS:
      return {
        ...state,
        contacts: {
          byId: action.payload.byId,
          allIds: action.payload.allIds,
          isLoading: false,
          error: null
        }
      };

    case actions.INSERT_CONTACT_SUCCESS:
    case actions.UPDATE_CONTACT_SUCCESS:
      // Actualizar tanto el contacto como la conversación asociada
      const updatedConversation = action.payload.conversationId
        ? state.conversations.byId[action.payload.conversationId]
        : null;

      return {
        ...state,
        contacts: {
          ...state.contacts,
          byId: {
            ...state.contacts.byId,
            [action.payload.conversationsContactId]: action.payload
          },
          allIds: state.contacts.allIds.includes(action.payload.conversationsContactId)
            ? state.contacts.allIds
            : [...state.contacts.allIds, action.payload.conversationsContactId],
          isLoading: false,
          error: null
        },
        conversations: updatedConversation
          ? {
              ...state.conversations,
              byId: {
                ...state.conversations.byId,
                [action.payload.conversationId]: {
                  ...updatedConversation,
                  ...action.payload
                }
              }
            }
          : state.conversations
      };

    case actions.FETCH_CONTACTS_FAILURE:
    case actions.INSERT_CONTACT_FAILURE:
    case actions.UPDATE_CONTACT_FAILURE:
      return {
        ...state,
        contacts: {
          ...state.contacts,
          isLoading: false,
          error: action.payload
        }
      };

    // ========== Conversations ==========
    case actions.FETCH_CONVERSATIONS_REQUEST:
    case actions.INSERT_CONVERSATION_REQUEST:
    case actions.UPDATE_CONVERSATION_REQUEST:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          isLoading: true,
          error: null
        }
      };

    case actions.FETCH_CONVERSATIONS_SUCCESS:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          byId: action.payload.byId,
          allIds: action.payload.allIds,
          isLoading: false,
          error: null
        }
      };

    case actions.INSERT_CONVERSATION_SUCCESS:
    case actions.UPDATE_CONVERSATION_SUCCESS:
      const existingConversation = state.conversations.byId[action.payload.conversationId];

      // Filtrar solo undefined (null es válido)
      const cleanedPayload = Object.entries(action.payload).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      return {
        ...state,
        conversations: {
          ...state.conversations,
          byId: {
            ...state.conversations.byId,
            [action.payload.conversationId]: existingConversation
              ? { ...existingConversation, ...cleanedPayload }
              : cleanedPayload
          },
          allIds: state.conversations.allIds.includes(action.payload.conversationId)
            ? state.conversations.allIds
            : [...state.conversations.allIds, action.payload.conversationId],
          isLoading: false,
          error: null
        }
      };

    case actions.FETCH_CONVERSATIONS_FAILURE:
    case actions.INSERT_CONVERSATION_FAILURE:
    case actions.UPDATE_CONVERSATION_FAILURE:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          isLoading: false,
          error: action.payload
        }
      };

    case actions.SELECT_CONVERSATION:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          selectedConversationId: action.payload
        }
      };

    // ========== Timeline ==========
    case actions.FETCH_TIMELINE_REQUEST:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            isLoading: true
          }
        }
      };

    case actions.FETCH_TIMELINE_SUCCESS:
      const totalItems =
        (action.payload.messages?.length || 0) +
        (action.payload.alerts?.length || 0) +
        (action.payload.callEvents?.length || 0);

      // Primera carga: asumir que hay más si hay contenido (el backend puede no devolver pageSize completo)
      const hasMoreItems = totalItems > 0;

      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            messages: action.payload.messages || [],
            alerts: action.payload.alerts || [],
            callEvents: action.payload.callEvents || [],
            groupMembers: action.payload.groupMembers || [],
            isLoading: false,
            currentPage: 1,
            hasMore: hasMoreItems
          }
        }
      };

    case actions.FETCH_TIMELINE_FAILURE:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            isLoading: false
          }
        }
      };

    case actions.LOAD_MORE_TIMELINE_REQUEST:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            isLoading: true
          }
        }
      };

    case actions.LOAD_MORE_TIMELINE_SUCCESS:
      // Filtrar duplicados por id
      const existingMessageIds = new Set(
        state.conversations.timeline.messages.map(
          (m) =>
            m.id ||
            ('conversationsInboundMessageId' in m
              ? m.conversationsInboundMessageId
              : m.conversationsOutboundMessageId)
        )
      );
      const newMessages = action.payload.messages.filter(
        (m) =>
          !existingMessageIds.has(
            m.id ||
              ('conversationsInboundMessageId' in m
                ? m.conversationsInboundMessageId
                : m.conversationsOutboundMessageId)
          )
      );

      const existingAlertIds = new Set(
        state.conversations.timeline.alerts.map((a) => a.id || a.conversationsChatAlertId)
      );
      const newAlerts = action.payload.alerts.filter(
        (a) => !existingAlertIds.has(a.id || a.conversationsChatAlertId)
      );

      const existingCallIds = new Set(
        state.conversations.timeline.callEvents.map((c) => c.id || c.conversationsCallChatEventId)
      );
      const newCallEvents = action.payload.callEvents.filter(
        (c) => !existingCallIds.has(c.id || c.conversationsCallChatEventId)
      );

      const totalLoadedItems =
        (action.payload.messages?.length || 0) +
        (action.payload.alerts?.length || 0) +
        (action.payload.callEvents?.length || 0);

      // Solo marcar hasMore=false si no se recibieron items (fin de datos)
      const hasMoreData = totalLoadedItems > 0;

      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            messages: [...newMessages, ...state.conversations.timeline.messages],
            alerts: [...newAlerts, ...state.conversations.timeline.alerts],
            callEvents: [...newCallEvents, ...state.conversations.timeline.callEvents],
            groupMembers: action.payload.groupMembers,
            isLoading: false,
            currentPage: state.conversations.timeline.currentPage + 1,
            hasMore: hasMoreData
          }
        }
      };

    case actions.LOAD_MORE_TIMELINE_FAILURE:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            isLoading: false
          }
        }
      };

    case actions.ADD_MESSAGE_TO_TIMELINE:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            messages: [...state.conversations.timeline.messages, action.payload]
          }
        }
      };

    case actions.ADD_ALERT_TO_TIMELINE:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            alerts: [...state.conversations.timeline.alerts, action.payload]
          }
        }
      };

    case actions.ADD_CALL_EVENT_TO_TIMELINE:
      // Evitar duplicados verificando si el ID ya existe
      const callEventExists = state.conversations.timeline.callEvents.some(
        (event: any) => event.id === action.payload.id
      );

      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            callEvents: callEventExists
              ? state.conversations.timeline.callEvents
              : [...state.conversations.timeline.callEvents, action.payload]
          }
        }
      };

    case actions.UPDATE_CALL_EVENT_STATUS_SUCCESS:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            callEvents: state.conversations.timeline.callEvents.map((event: any) =>
              event.id === `CallChatEvent-${action.payload.callEventId}`
                ? { ...event, status: action.payload.status }
                : event
            )
          }
        }
      };

    // ========== Messages ==========
    case actions.SEND_MESSAGE_REQUEST:
      return state;

    case actions.SEND_MESSAGE_SUCCESS:
      // No agregar el mensaje aquí - esperar a que FETCH_TIMELINE_SUCCESS actualice todo
      return state;

    case actions.RECEIVE_MESSAGE:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            messages: [...state.conversations.timeline.messages, action.payload]
          }
        }
      };

    case actions.UPDATE_MESSAGE_STATUS:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            messages: state.conversations.timeline.messages.map((msg) =>
              'conversationsOutboundMessageId' in msg &&
              msg.conversationsOutboundMessageId === action.payload.conversationsOutboundMessageId
                ? { ...msg, ...action.payload }
                : msg
            )
          }
        }
      };

    case actions.MARK_MESSAGE_READ:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          timeline: {
            ...state.conversations.timeline,
            messages: state.conversations.timeline.messages.map((msg) =>
              'conversationsInboundMessageId' in msg &&
              msg.conversationsInboundMessageId === action.payload.conversationsInboundMessageId
                ? { ...msg, isRead: action.payload.isRead }
                : msg
            )
          }
        }
      };

    case actions.PIN_CONVERSATION: {
      const MAX_VISIBLE_TILES = 4;
      const conversationId = action.payload;
      const currentPinned = state.conversations.pinnedConversationIds || [];

      // Si ya está pinned, no hacer nada
      if (currentPinned.includes(conversationId)) {
        return state;
      }

      let newPinned = [...currentPinned];

      // Si hay 4 tiles, quitar el más antiguo
      if (newPinned.length >= MAX_VISIBLE_TILES) {
        newPinned = newPinned.slice(1);
      }

      // Agregar el nuevo al final
      newPinned.push(conversationId);
      savePinnedToLocalStorage(newPinned);

      return {
        ...state,
        conversations: {
          ...state.conversations,
          pinnedConversationIds: newPinned
        }
      };
    }

    case actions.UNPIN_CONVERSATION: {
      const currentPinned = state.conversations.pinnedConversationIds || [];
      const newPinned = currentPinned.filter((id) => id !== action.payload);
      savePinnedToLocalStorage(newPinned);

      return {
        ...state,
        conversations: {
          ...state.conversations,
          pinnedConversationIds: newPinned
        }
      };
    }

    case actions.LOAD_PINNED_CONVERSATIONS:
      return {
        ...state,
        conversations: {
          ...state.conversations,
          pinnedConversationIds: action.payload
        }
      };

    // ========== Templates ==========
    case actions.FETCH_TEMPLATES_REQUEST:
      return {
        ...state,
        templates: {
          ...state.templates,
          isLoading: true,
          error: null
        }
      };

    case actions.FETCH_TEMPLATES_SUCCESS:
      return {
        ...state,
        templates: {
          items: action.payload,
          isLoading: false,
          error: null
        }
      };

    case actions.FETCH_TEMPLATES_FAILURE:
      return {
        ...state,
        templates: {
          ...state.templates,
          isLoading: false,
          error: action.payload
        }
      };

    case actions.SEND_TEMPLATE_REQUEST:
      return state;

    case actions.SEND_TEMPLATE_SUCCESS:
    case actions.SEND_TEMPLATE_FAILURE:
      return state;

    default:
      return state;
  }
};
